/**
 * Account Settings Component
 * The Account module's page (#92): profile and security tabs.
 *
 * The route is the open/closed and active-tab state; this component only
 * renders. Tab clicks go through the tab-select handler so the URL stays
 * the single source of truth.
 */

import { useUserStore } from '../stores/user-store';
import type UserManagementService from '../core/user-management-service';
import { escapeHtml } from '../core/utilities/dom';
import { BaseComponent, type BaseComponentOptions } from './base-component';
import { errorMessage } from '../core/forbidden-message';

export type AccountSettingsTab = 'profile' | 'security';

export interface AccountSettingsOptions extends BaseComponentOptions {
  userService?: UserManagementService;
  onTabSelect?: (tab: AccountSettingsTab) => void;
}

class AccountSettings extends BaseComponent {
  private userService?: UserManagementService;
  private currentTab: AccountSettingsTab;
  private onTabSelect: (tab: AccountSettingsTab) => void;
  private unsubscribeUser?: () => void;

  constructor(options: AccountSettingsOptions = {}) {
    super(options);
    this.userService = options.userService;
    this.currentTab = 'profile';
    this.onTabSelect = options.onTabSelect ?? ((tab) => this.switchTab(tab));
  }

  protected getDefaultCssClass(): string {
    return 'account-settings';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'accountSettings.email': 'Email',
      'accountSettings.firstName': 'First Name',
      'accountSettings.lastName': 'Last Name',
      'accountSettings.save': 'Save Changes',
      'accountSettings.saving': 'Saving...',
      'accountSettings.saved': 'Changes saved',
      'accountSettings.changePassword': 'Change Password',
      'accountSettings.changePasswordDesc':
        "Change your password in your secure account portal. You'll be asked to sign in again first.",
      'accountSettings.manageMfa': 'Manage two-factor authentication',
      'accountSettings.mfaInfo':
        'Two-factor authentication is managed in your secure account portal.',
      'accountSettings.error': 'An error occurred',
      'accountSettings.memberSince': 'Member since',
    };
  }

  protected override onInitialize(): void {
    this.attachStaticEventListeners();
    this.attachDynamicEventListeners();
    this.subscribeToUser();
  }

  /** Re-render when user data changes. Idempotent. */
  private subscribeToUser(): void {
    if (this.unsubscribeUser) return;
    this.unsubscribeUser = useUserStore.subscribe((state, prevState) => {
      if (
        state.user !== prevState.user ||
        state.isUpdating !== prevState.isUpdating
      ) {
        this.updateContent();
      }
    });
  }

  /** Stop re-rendering while the Account module is hidden (#92). */
  suspend(): void {
    this.unsubscribeUser?.();
    this.unsubscribeUser = undefined;
  }

  /**
   * Re-sync from the store, then listen again. The re-sync is
   * load-bearing: changes made while suspended were missed.
   */
  resume(): void {
    this.updateContent();
    this.subscribeToUser();
  }

  setTabSelectHandler(handler: (tab: AccountSettingsTab) => void): void {
    this.onTabSelect = handler;
  }

  /**
   * Bind to the page markup and render the initial tab content.
   */
  protected override render(): void {
    if (!this.container) return;
    const body = this.container.querySelector(`.${this.baseCssClass}__body`);
    if (body) {
      body.innerHTML = this.renderTabContent();
    }
  }

  /**
   * Render the current tab content
   */
  private renderTabContent(): string {
    switch (this.currentTab) {
      case 'profile':
        return this.renderProfileTab();
      case 'security':
        return this.renderSecurityTab();
      default:
        return '';
    }
  }

  /**
   * Render Profile tab
   */
  private renderProfileTab(): string {
    const user = useUserStore.getState().user;
    const isUpdating = useUserStore.getState().isUpdating;

    if (!user) {
      return `<div class="${this.baseCssClass}__loading">Loading...</div>`;
    }

    return `
      <div class="${this.baseCssClass}__panel" role="tabpanel" data-panel="profile">
        <form class="${this.baseCssClass}__form" data-form="profile">
          <div class="${this.baseCssClass}__field">
            <label class="${this.baseCssClass}__label">${this.t('accountSettings.email')}</label>
            <input type="email" class="${this.baseCssClass}__input" value="${escapeHtml(user.email || '')}" disabled readonly />
          </div>
          <div class="${this.baseCssClass}__field">
            <label class="${this.baseCssClass}__label">${this.t('accountSettings.firstName')}</label>
            <input type="text" class="${this.baseCssClass}__input" name="firstName" value="${escapeHtml(user.firstName || '')}" />
          </div>
          <div class="${this.baseCssClass}__field">
            <label class="${this.baseCssClass}__label">${this.t('accountSettings.lastName')}</label>
            <input type="text" class="${this.baseCssClass}__input" name="lastName" value="${escapeHtml(user.lastName || '')}" />
          </div>
          <div class="${this.baseCssClass}__actions">
            <button type="submit" class="${this.baseCssClass}__button ${this.baseCssClass}__button--primary" ${isUpdating ? 'disabled' : ''}>
              ${isUpdating ? this.t('accountSettings.saving') : this.t('accountSettings.save')}
            </button>
          </div>
          <div class="${this.baseCssClass}__message" data-message="profile"></div>
        </form>

        <div class="${this.baseCssClass}__section">
          <h3 class="${this.baseCssClass}__section-title">${this.t('accountSettings.changePassword')}</h3>
          <p class="${this.baseCssClass}__section-desc">${this.t('accountSettings.changePasswordDesc')}</p>
          <button class="${this.baseCssClass}__button" data-action="change-password">
            ${this.t('accountSettings.changePassword')}
          </button>
          <div class="${this.baseCssClass}__message" data-message="password"></div>
        </div>
      </div>
    `;
  }

  /**
   * Render Security tab — MFA is managed in Ory's hosted Account Experience.
   */
  private renderSecurityTab(): string {
    return `
      <div class="${this.baseCssClass}__panel" role="tabpanel" data-panel="security">
        <div class="${this.baseCssClass}__section">
          <p class="${this.baseCssClass}__section-desc">${this.t('accountSettings.mfaInfo')}</p>
          <button class="${this.baseCssClass}__button ${this.baseCssClass}__button--primary" data-action="manage-mfa">
            ${this.t('accountSettings.manageMfa')}
          </button>
          <div class="${this.baseCssClass}__message" data-message="mfa"></div>
        </div>
      </div>
    `;
  }

  /**
   * Attach static event listeners (called once during initialization).
   */
  private attachStaticEventListeners(): void {
    if (!this.container) return;
    const tabs = this.container.querySelectorAll(`.${this.baseCssClass}__tab`);
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.onTabSelect(tab.getAttribute('data-tab') as AccountSettingsTab);
      });
    });
  }

  /**
   * Attach dynamic event listeners (called when tab content changes)
   * These are for elements inside the tab body that get re-rendered
   */
  private attachDynamicEventListeners(): void {
    if (!this.container) return;

    // Profile form
    const profileForm = this.container.querySelector('[data-form="profile"]');
    profileForm?.addEventListener('submit', (e) => this.handleProfileSubmit(e));

    // Change password (redirect to Ory hosted settings)
    const changePasswordBtn = this.container.querySelector(
      '[data-action="change-password"]'
    );
    changePasswordBtn?.addEventListener('click', () =>
      this.handleChangePassword()
    );

    // Manage MFA (redirect to Ory hosted account settings)
    const manageMfaBtn = this.container.querySelector(
      '[data-action="manage-mfa"]'
    );
    manageMfaBtn?.addEventListener('click', () => this.handleManageMfa());
  }

  /**
   * Switch to a different tab
   */
  switchTab(tab: AccountSettingsTab): void {
    if (this.currentTab === tab) return; // Already on this tab
    this.currentTab = tab;
    this.updateContent();
  }

  /**
   * Update content (re-render body)
   */
  updateContent(): void {
    const body = this.container?.querySelector(`.${this.baseCssClass}__body`);
    if (body) {
      body.innerHTML = this.renderTabContent();
      this.attachDynamicEventListeners();
    }

    // Update tab active states
    const tabs = this.container?.querySelectorAll(`.${this.baseCssClass}__tab`);
    tabs?.forEach((tab) => {
      const tabName = tab.getAttribute('data-tab');
      tab.classList.toggle(
        `${this.baseCssClass}__tab--active`,
        tabName === this.currentTab
      );
      tab.setAttribute('aria-selected', String(tabName === this.currentTab));
    });
  }

  /**
   * Show message in a tab
   */
  private showMessage(
    messageId: string,
    text: string,
    type: 'success' | 'error' = 'success'
  ): void {
    const messageEl = this.container?.querySelector(
      `[data-message="${messageId}"]`
    );
    if (messageEl) {
      messageEl.textContent = text;
      messageEl.className = `${this.baseCssClass}__message ${this.baseCssClass}__message--${type}`;
    }
  }

  /**
   * Handle profile form submit
   */
  private async handleProfileSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!this.userService) return;

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;

    useUserStore.getState().setUpdating(true);

    try {
      const updatedUser = await this.userService.updateProfile({
        firstName,
        lastName,
      });
      useUserStore.getState().setUser(updatedUser);
      this.showMessage('profile', this.t('accountSettings.saved'), 'success');
    } catch (error) {
      const message = errorMessage(
        error,
        (k) => this.t(k),
        this.t('accountSettings.error')
      );
      this.showMessage('profile', message, 'error');
    } finally {
      useUserStore.getState().setUpdating(false);
    }
  }

  /**
   * Redirect to Ory's hosted settings flow to change the password. Ory asks the
   * user to sign in again first.
   */
  private async handleChangePassword(): Promise<void> {
    if (!this.userService) return;
    try {
      const url = await this.userService.getChangePasswordUrl();
      this.navigate(url);
    } catch (error) {
      const message = errorMessage(
        error,
        (k) => this.t(k),
        this.t('accountSettings.error')
      );
      this.showMessage('password', message, 'error');
    }
  }

  /**
   * Redirect to Ory's hosted account settings to manage two-factor auth.
   */
  private async handleManageMfa(): Promise<void> {
    if (!this.userService) return;
    try {
      const url = await this.userService.getAccountSettingsUrl();
      this.navigate(url);
    } catch (error) {
      const message = errorMessage(
        error,
        (k) => this.t(k),
        this.t('accountSettings.error')
      );
      this.showMessage('mfa', message, 'error');
    }
  }

  /**
   * Send the browser to an external (Ory) page. A method so tests can stub it:
   * jsdom does not allow spying on window.location.assign.
   */
  protected navigate(url: string): void {
    window.location.assign(url);
  }

  /**
   * Set user service reference
   */
  setUserService(userService: UserManagementService): void {
    this.userService = userService;
  }

  /**
   * Update UI text when language changes.
   * Shell text uses data-i18n (handled by UIManager); re-render tab content for dynamic strings.
   */
  override updateUILanguage(): void {
    this.updateContent();
  }

  override destroy(): void {
    this.suspend();
    super.destroy();
  }
}

export default AccountSettings;
